import React, { useCallback, useMemo } from "react";
import { clamp } from "@common/helpers/collections";
import { pluralize } from "@common/helpers/string";
import classNames from "classnames";
import {
    FLAT_RATE_PLANS,
    ENTITY_TRANSACTIONS_THRESHOLD,
} from "@common/flatRateBilling";
import {
    currencyFormatterFractionsIfNeeded,
    currencyFormatterNoFractions,
    currencyFormatterShort,
} from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { getBillingIntervalAdjective } from "@common/billing";
import { addYears, format } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useOptionalSupportChat } from "@components/supportChat/useSupportChat";
import { Card } from "@design-system/Card/Card";
import { useConstant } from "@hooks/useConstant";
import { Button } from "@design-system/Button/Button";
import { CustomProgressBar } from "@design-system/CustomProgressBar/CustomProgressBar";
import { Section } from "@design-system/Section/Section";
import { useSettings } from "../../SettingsProvider";
import { EndTrialAction } from "../../../billing/AccessBlockedPage/EndTrialAction";
import { CurrentPlanHeader } from "./CurrentPlanHeader";
import { CurrentPlanFooter } from "./CurrentPlanFooter";
import styles from "./FlatRateBilling.module.scss";
import { useTransferRequestsForClientResolution } from "./useTransferRequestsForClientResolution";
import { PendingTransferRequest } from "./PendingTransferRequest";
import { AdditionalEntitiesSection } from "./AdditionalEntitiesSection";

export const CurrentPlan: React.FC = () => {
    const { open, close } = useSettings();
    const supportChat = useOptionalSupportChat();
    const {
        currentPlan,
        isV2Plan,
        isV3Plan,
        transactionCount,
        plans,
        totalUsage,
        isFetched,
        isTrialing,
        subscriptionDetails,
    } = useBillingStatus();
    const transferRequests = useTransferRequestsForClientResolution();

    const isEnterprisePlan = currentPlan === FLAT_RATE_PLANS.ENTERPRISE;
    // PLUS workspaces are billed per-entity on both V2 (metered) and V3
    // (licensed quantity), so the entity-count breakdown is meaningful on
    // both catalogs. The underlying entity-billing-count API is V2+V3 aware.
    const shouldShowEntityBillingCount =
        currentPlan === FLAT_RATE_PLANS.PLUS && (isV2Plan || isV3Plan);
    const handleBillingButton = useCallback(() => {
        if (isEnterprisePlan) {
            supportChat?.openSupportChat();
            close();
        } else {
            open(["plans"]);
        }
    }, [close, isEnterprisePlan, open, supportChat]);

    const now = useConstant(() => new Date());
    const renewsOn = useMemo(() => {
        if (!subscriptionDetails?.startedAt || !isEnterprisePlan) {
            return null;
        }
        let temporaryValue = addYears(subscriptionDetails.startedAt, 1);
        while (temporaryValue < now) {
            temporaryValue = addYears(temporaryValue, 1);
        }
        return temporaryValue;
    }, [subscriptionDetails, isEnterprisePlan, now]);

    if (!isFetched) {
        return null;
    }

    const currentPlanData = plans.find((plan) => plan.plan === currentPlan)!;
    const maxUsage = currentPlanData.usageLimit ?? 0;

    const isAtOrOverLimit = totalUsage >= maxUsage;
    const isOnFreePlan = currentPlan === FLAT_RATE_PLANS.FREE;
    const clampedUsage = clamp(totalUsage, 0, maxUsage);

    const monthlyAmount =
        subscriptionDetails?.monthlyPriceCents ??
        currentPlanData.monthlyAmountCents;

    const discountedMonthlyAmount =
        subscriptionDetails?.discountedMonthlyAmountCents ?? monthlyAmount;

    const intervalAmount =
        subscriptionDetails?.intervalAmountCents ??
        currentPlanData.intervalAmountCents;

    const discountedIntervalAmount =
        subscriptionDetails?.discountedIntervalAmountCents ?? monthlyAmount;

    return (
        <Section heading="Current Plan">
            <Card className={styles.card}>
                <section className={styles.section}>
                    <main>
                        <div>
                            <CurrentPlanHeader />

                            <div>
                                <header className="mb-2">
                                    {discountedMonthlyAmount !==
                                        monthlyAmount && (
                                        <s className="mr-2">
                                            {currencyFormatterFractionsIfNeeded.format(
                                                fromIntToMonetary(
                                                    monthlyAmount,
                                                ),
                                            )}
                                        </s>
                                    )}
                                    {currencyFormatterFractionsIfNeeded.format(
                                        fromIntToMonetary(
                                            discountedMonthlyAmount,
                                        ),
                                    )}
                                    /m
                                </header>

                                {subscriptionDetails && (
                                    <>
                                        <div className={styles.additionalInfo}>
                                            {discountedIntervalAmount !==
                                                intervalAmount && (
                                                <s className="mr-2">
                                                    {currencyFormatterFractionsIfNeeded.format(
                                                        fromIntToMonetary(
                                                            intervalAmount,
                                                        ),
                                                    )}
                                                </s>
                                            )}
                                            {currencyFormatterFractionsIfNeeded.format(
                                                fromIntToMonetary(
                                                    discountedIntervalAmount,
                                                ),
                                            )}{" "}
                                            billed{" "}
                                            {getBillingIntervalAdjective(
                                                subscriptionDetails?.interval ??
                                                    "month",
                                                subscriptionDetails?.intervalCount ??
                                                    1,
                                            )}
                                            {isEnterprisePlan && renewsOn
                                                ? ` - Renews on ${format(renewsOn, fullDateLongFormat)}`
                                                : ""}
                                        </div>
                                        {isEnterprisePlan && (
                                            <div
                                                className={classNames(
                                                    "mt-2",
                                                    styles.additionalInfo,
                                                )}
                                            >
                                                Kick Custom is an annual
                                                commitment. To modify your
                                                subscription please reach out to
                                                your dedicated team via chat.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <aside className={styles.actions}>
                            {isOnFreePlan ? (
                                <Button
                                    variant="tertiary"
                                    size="md"
                                    onClick={() => open(["plans"])}
                                    data-testid="upgrade-button"
                                >
                                    Upgrade
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => handleBillingButton()}
                                    data-testid="update-plan-button"
                                >
                                    {isEnterprisePlan
                                        ? "Get support"
                                        : "Update plan"}
                                </Button>
                            )}

                            {isTrialing && (
                                <EndTrialAction variant="primary">
                                    Start subscription
                                </EndTrialAction>
                            )}
                        </aside>
                    </main>
                </section>
                {transferRequests.map((transferRequest) => (
                    <PendingTransferRequest
                        key={transferRequest.request.code}
                        request={transferRequest.request}
                        organization={transferRequest.organization}
                    />
                ))}
                {shouldShowEntityBillingCount && <AdditionalEntitiesSection />}
                {currentPlan === FLAT_RATE_PLANS.FREE && (
                    <section
                        className={classNames(
                            styles.section,
                            styles.progressBarSection,
                        )}
                    >
                        {isV2Plan || isV3Plan ? (
                            <main>
                                <span className="text-left">0</span>
                                <span className="text-right">
                                    {ENTITY_TRANSACTIONS_THRESHOLD}{" "}
                                    {pluralize(
                                        "transaction",
                                        ENTITY_TRANSACTIONS_THRESHOLD,
                                    )}
                                </span>
                                <CustomProgressBar
                                    className={styles.progressBar}
                                    value={Math.min(
                                        transactionCount,
                                        ENTITY_TRANSACTIONS_THRESHOLD,
                                    )}
                                    max={ENTITY_TRANSACTIONS_THRESHOLD}
                                    renderValue={(value) => (
                                        <>
                                            {value}{" "}
                                            {pluralize("transaction", value)}
                                        </>
                                    )}
                                />
                            </main>
                        ) : (
                            <main>
                                <span className="text-left">
                                    {currencyFormatterNoFractions.format(0)}
                                </span>
                                <span className="text-right">
                                    {currencyFormatterNoFractions.format(
                                        maxUsage,
                                    )}
                                </span>
                                <CustomProgressBar
                                    className={styles.progressBar}
                                    value={clampedUsage}
                                    max={maxUsage}
                                    renderValue={(value) => (
                                        <>
                                            {currencyFormatterShort
                                                .format(value)
                                                .toLowerCase()}{" "}
                                            business expenses
                                        </>
                                    )}
                                />
                            </main>
                        )}
                    </section>
                )}
                <CurrentPlanFooter
                    isOnFreePlan={isOnFreePlan}
                    isAtOrOverLimit={isAtOrOverLimit}
                    maxUsage={maxUsage}
                    isThresholdEnforcedPlan={isV2Plan || isV3Plan}
                    isAtTransactionLimit={
                        transactionCount >= ENTITY_TRANSACTIONS_THRESHOLD
                    }
                />
            </Card>
        </Section>
    );
};
